import { useState, useEffect, type ReactNode } from 'react'
import { EmptyState, Button, StatusHint } from '@indipia/ui-core'
import { useAppStore, useAuthStore } from '@indipia/shared'
import { ExternalLinkSend, ShareLinkPopup, shareViaKakao, type Recipient } from '@indipia/shared-features'
import type { NavFn } from '../pages/SchedulerPage'
import type { SiteInfo } from './CalendarMultiView'
import { scheduleApi } from '../api/scheduleApi'
import { fetchCustomerShareLink } from '../api/customerShareLinkApi'
import { fetchRecipientWorkTypes } from '../api/scheduleShareApi'
import { useScheduleSharePreview } from '../hooks/useScheduleSharePreview'
import { ScheduleSharePreviewPanel } from './ScheduleSharePreviewPanel'

type Props = {
  navigate: NavFn
  selectedSite?: SiteInfo
  /** 서버 판정(종료일+7일 이내) — false 면 [알림톡 보내기]만 비활성. 모름(undefined)이면 막지 않는다(발송 API 가 다시 판정). */
  shareSendable?: boolean
}

/** 서버가 준 만료 시각('YYYY-MM-DDT…')을 M/D 로 표기 — 날짜 계산 없이 문자열만 나눈다. */
function toMonthDay(expiresAt: string | null | undefined): string {
  if (!expiresAt) return ''
  const [, m, d] = expiresAt.slice(0, 10).split('-')
  return m && d ? `${Number(m)}/${Number(d)}` : ''
}

export function KakaoShareView({ selectedSite, shareSendable }: Props) {
  const user = useAuthStore(s => s.user)
  // authStore.user.compName은 로그인 시점에 채워지지 않는 필드라 항상 빈 값 — AppFrame이 이미
  // /member/auth/session으로 받아 채워두는 appStore.session.compName을 대신 쓴다.
  const compName = useAppStore(s => s.session?.compName) ?? user?.compName ?? ''

  const [sharePopupOpen, setSharePopupOpen] = useState(false)
  useEffect(() => { setSharePopupOpen(false) }, [selectedSite?.scheduleId])

  // 수신자 목록은 ExternalLinkSend 가 들고 있고, 미리보기·유효기간 안내는 서버 응답으로만 그린다.
  const [recipients, setRecipients] = useState<Recipient[]>([])
  // ExternalLinkSend 는 현장(key)마다 새로 마운트되며 마운트 시 수신자 목록을 다시 알려준다.
  const { customer: customerPreview, general: generalPreview, generalTarget } =
    useScheduleSharePreview(selectedSite?.siteId, selectedSite?.customerName ?? '', recipients)

  if (!selectedSite) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <EmptyState title="현장을 선택하세요" description="우측 패널에서 현장을 선택하면 발송 화면이 열립니다" />
      </div>
    )
  }

  // 종료일+7일 경과 현장(서버 shareSendable=false) — 알림톡만 막고 [공유하기](링크 복사)는 연다.
  const sendBlocked = shareSendable === false
  const endMd = toMonthDay(selectedSite.endDate)

  // 유효기간은 서버가 산정한다 — 입력 대신 읽기 전용 안내. 날짜 값은 미리보기 응답의 expiresAt 만 쓴다.
  const customerMd = toMonthDay(customerPreview.data?.expiresAt)
  const generalMd = toMonthDay(generalPreview.data?.expiresAt)
  // 좌(고객) · 우(반장·거래처) 영역에 각자의 링크 유효기간을 읽기 전용으로 안내(서버 산정)
  const customerExpiryNotice: ReactNode = (
    <StatusHint tone="info">고객: 공사 종료일 + 7일{customerMd ? `(${customerMd})` : ''}까지</StatusHint>
  )
  const expiryNotice: ReactNode = (
    <StatusHint tone="info">반장·거래처: 해당 공종 종료일{generalMd ? `(${generalMd})` : ''}까지</StatusHint>
  )

  return (
    <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, boxSizing: 'border-box' }}>
    <div className="lay-w-lg" style={{ width: '100%', display: 'flex', gap: 20, alignItems: 'flex-start' }}>

      {/* 유효기간 안내·수신자·발송 — 공통 컴포넌트 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* 현재 전송 대상 현장 — ExternalLinkSend는 여러 에이전트가 공유하는 범용 컴포넌트라
            현장명을 자체 표시하지 않으므로, 화면 상단에 별도로 보여준다. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', marginBottom: 16, borderRadius: 16, border: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)' }}>
          <div style={{ display: 'flex', width: 32, height: 32, alignItems: 'center', justifyContent: 'center', borderRadius: 12, background: 'var(--gt-subtle)', color: 'var(--gt-primary)', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10.5, color: 'var(--shell-hint)' }}>전송 대상 현장</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--shell-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedSite.siteName || '—'}</div>
          </div>
        </div>
        {sendBlocked && (
          <div className="mb-3">
            <StatusHint tone="warning">
              {`공사 종료일${endMd ? `(${endMd})` : ''}에서 7일이 지나 알림톡은 보낼 수 없어요. 링크를 복사해 직접 보내 주세요.`}
            </StatusHint>
          </div>
        )}
        <ExternalLinkSend
          key={selectedSite.scheduleId}
          sourceService="sv-indipia"
          sourceAgent="E01"
          resourceType="SCHEDULE"
          shareType="SCHEDULE_SHARE"
          shareScope="READ"
          authType="NONE"
          showMessageForm={false}
          expiryNotice={expiryNotice}
          customerExpiryNotice={customerExpiryNotice}
          resolveRecipientWorkTypes={(r) => fetchRecipientWorkTypes(selectedSite.scheduleId, r.recipientType, r.id!)}
          customerOnlyDirectInput
          onRecipientsChange={setRecipients}
          initialRecipients={
            selectedSite.customerName && selectedSite.contact
              ? [{ name: selectedSite.customerName, mobile: selectedSite.contact, recipientType: 'CUSTOMER' as const }]
              : undefined
          }
          resolveResourceRef={async () => String(selectedSite.scheduleId)}
          // 승인 템플릿의 "공유 일정 확인하기" 버튼 — 서버가 ShareGate에서 수신자별 URL로 발급해 채움
          resolveButtons={() => [{ type: 'WL', name: '공유 일정 확인하기', resourceRef: String(selectedSite.scheduleId) }]}
          // 문구·유효기간·이력 저장은 서버가 처리한다 — 화면은 서버가 문구를 채울 값(회사·공유자·현장)만 보낸다.
          initialVariables={{
            company_name: compName,
            member_name:  user?.memberName ?? '',
            site_address: selectedSite.siteName ?? '',
          }}
          // 발송 목적지를 sv-schedule로 교체 — 공용 컴포넌트/훅 기본동작은 불변(W5-b).
          sendOverride={(p) => scheduleApi.sendScheduleShare(p)}
          sendLabel="알림톡 보내기"
          sendDisabled={sendBlocked}
          extraActions={
            <Button variant="secondary" type="button" onClick={() => setSharePopupOpen(true)} className="flex-none">
              공유하기
            </Button>
          }
        />
      </div>

      <ShareLinkPopup
        open={sharePopupOpen}
        onClose={() => setSharePopupOpen(false)}
        siteId={selectedSite.siteId}
        fetchLink={() => fetchCustomerShareLink(selectedSite.siteId)}
        onKakaoShare={(url) => shareViaKakao({
          url,
          title: `${selectedSite.siteName} 공사 일정`,
          description: compName ? `${compName}에서 공유한 공사 일정이에요` : '공유된 공사 일정이에요',
          imageUrl: `${window.location.origin}/brand/indipia-logo.png`,
        })}
      />

      {/* 카카오 알림톡 미리보기 */}
      <section style={{ width: 300, flexShrink: 0, position: 'sticky', top: 24, borderRadius: 16, border: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ display: 'flex', width: 32, height: 32, alignItems: 'center', justifyContent: 'center', borderRadius: 12, background: 'var(--gt-subtle)', color: 'var(--gt-primary)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12"/></svg>
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--shell-on-surface)' }}>카카오 알림톡 미리보기</span>
        </div>
        <ScheduleSharePreviewPanel
          customer={customerPreview}
          general={generalPreview}
          generalTarget={generalTarget}
        />
      </section>
    </div>
    </div>
  )
}
